// frontend/shared-ui/src/forms/ShipmentContextSection.jsx
import React, { useState } from "react";
import { formatMoney } from "../utils/money";
import { useShipmentContext } from "./useShipmentContext";

export default function ShipmentContextSection({
  expanded: defaultExpanded = false,
}) {
  const {
    context,
    setField,
    reset,
    freightPerCbm,
    overheadTotal,
    overheadPerCbm,
    fullPerCbm,
  } = useShipmentContext();

  const [expanded, setExpanded] = useState(defaultExpanded);

  const totalCbm = Number(context.totalCbm) || 0;
  const totalShipmentCost = Number(context.totalShipmentCost) || 0;
  const freightTotal =
    totalShipmentCost > 0
      ? totalShipmentCost
      : totalCbm * (Number(context.costPerCbm) || 0);

  return (
    <div className="fh-shipment-ctx">
      <button
        type="button"
        className="fh-shipment-ctx__toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="fh-shipment-ctx__title">
          Shipment costs
          {context.label && (
            <span className="fh-shipment-ctx__label"> · {context.label}</span>
          )}
        </span>
        <span className="fh-shipment-ctx__summary">
          {totalCbm > 0
            ? `${totalCbm} CBM · ${formatMoney(fullPerCbm, { symbol: "$", decimals: 2 })}/CBM`
            : "not set"}
          <span
            className={`fh-shipment-ctx__chev ${expanded ? "is-open" : ""}`}
          >
            ▾
          </span>
        </span>
      </button>

      {expanded && (
        <div className="fh-shipment-ctx__body">
          <div className="fh-shipment-ctx__grid">
            <div className="fh-form-field" style={{ gridColumn: "1 / -1" }}>
              <label className="fh-form-field__label">Label</label>
              <input
                className="fh-input"
                value={context.label}
                onChange={(e) => setField("label", e.target.value)}
                placeholder="e.g. APT Batch #1, Oct 2026"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">Total CBM</label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.totalCbm}
                onChange={(e) =>
                  setField("totalCbm", Number(e.target.value) || 0)
                }
                placeholder="14.5"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">
                Total shipment cost
              </label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.totalShipmentCost}
                onChange={(e) =>
                  setField("totalShipmentCost", Number(e.target.value) || 0)
                }
                placeholder="4500"
              />
              <div className="fh-form-field__hint">
                Freight + all costs paid for the whole shipment
              </div>
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">Rent</label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.rent}
                onChange={(e) => setField("rent", Number(e.target.value) || 0)}
                placeholder="0"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">Customs</label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.customs}
                onChange={(e) =>
                  setField("customs", Number(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">Insurance</label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.insurance}
                onChange={(e) =>
                  setField("insurance", Number(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">Misc</label>
              <input
                className="fh-input"
                type="number"
                step="0.01"
                value={context.misc}
                onChange={(e) => setField("misc", Number(e.target.value) || 0)}
                placeholder="0"
              />
            </div>

            <div className="fh-form-field">
              <label className="fh-form-field__label">
                Default target margin %
              </label>
              <input
                className="fh-input"
                type="number"
                step="1"
                value={context.targetMarginPct}
                onChange={(e) =>
                  setField("targetMarginPct", Number(e.target.value) || 0)
                }
                placeholder="30"
              />
            </div>
          </div>

          <div className="fh-shipment-ctx__summary-box">
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Freight
                {totalCbm > 0 && totalShipmentCost > 0 && (
                  <>
                    {" "}
                    (
                    {formatMoney(totalShipmentCost, {
                      symbol: "$",
                      decimals: 2,
                    })}{" "}
                    ÷ {totalCbm} CBM)
                  </>
                )}
              </div>
              <strong>
                {formatMoney(freightPerCbm, { symbol: "$", decimals: 2 })}/CBM
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Overhead
              </div>
              <strong>
                {formatMoney(overheadPerCbm, { symbol: "$", decimals: 2 })}/CBM
              </strong>
              <div className="text-muted" style={{ fontSize: 10 }}>
                total {formatMoney(overheadTotal, { symbol: "$", decimals: 2 })}
                )
              </div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Effective cost per CBM
              </div>
              <strong style={{ color: "var(--fh-primary)" }}>
                {formatMoney(fullPerCbm, { symbol: "$", decimals: 2 })}
              </strong>
            </div>
          </div>

          <div className="mt-2">
            <button
              type="button"
              className="fh-btn fh-btn--ghost fh-btn--sm"
              onClick={reset}
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
